import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus, Briefcase, ChevronLeft, Circle, CheckCircle2, Clock, Flag, ListTodo, Columns3, GitBranch, Users2, Loader2 } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Loading, EmptyState, HEALTH_COLORS } from "../components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUSES = ["Draft", "Planned", "Active", "On Hold", "Completed", "Closed", "Cancelled"];
const TASK_STATUS = { todo: "To do", in_progress: "In progress", done: "Done" };
const HEALTH_LABEL = { green: "On track", amber: "Watch", red: "At risk" };

export default function Engagements() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", org_name: "", template_key: "standard" });

  const { data: list, isLoading } = useQuery({ queryKey: ["engagements"], queryFn: async () => (await api.get("/engagements")).data });
  const { data: templates } = useQuery({ queryKey: ["engagement-templates"], queryFn: async () => (await api.get("/engagements/templates")).data, enabled: createOpen });
  const create = useMutation({ mutationFn: () => api.post("/engagements", { ...form, status: "Planned" }), onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["engagements"] }); setCreateOpen(false); setForm({ title: "", org_name: "", template_key: "standard" }); setSelected(r.data.id); } });

  if (isLoading) return <Loading />;
  if (selected) return <EngagementDetail id={selected} onBack={() => setSelected(null)} />;

  return (
    <div>
      <PageHeader eyebrow="Engagement Delivery" title="Engagements"
        description="Deliver each engagement through phases, tasks and milestones with a transparent delivery health indicator."
        actions={<Button onClick={() => setCreateOpen(true)} data-testid="new-engagement" className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-1.5" /> New engagement</Button>} />
      {(list || []).length === 0 ? (
        <EmptyState icon={Briefcase} title="No engagements yet" description="Engagements are created when a proposal is accepted, or start one manually." action={<Button onClick={() => setCreateOpen(true)} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-1.5" /> New engagement</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((e) => {
            const done = e.tasks.filter((t) => t.status === "done").length;
            const pct = e.tasks.length ? Math.round((done / e.tasks.length) * 100) : 0;
            return (
              <button key={e.id} onClick={() => setSelected(e.id)} data-testid={`engagement-card-${e.id}`}
                className="text-left bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md hover:border-emerald-200 p-6 transition-all">
                <div className="flex items-center justify-between">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{e.status}</span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-500"><span className={`w-2 h-2 rounded-full ${HEALTH_COLORS[e.health]}`} /> {HEALTH_LABEL[e.health]}</span>
                </div>
                <div className="font-display font-semibold text-slate-900 mt-3">{e.title}</div>
                <div className="text-sm text-slate-500">{e.org_name}</div>
                <div className="mt-4"><div className="flex justify-between text-xs text-slate-400 mb-1"><span>{done}/{e.tasks.length} tasks</span><span>{pct}%</span></div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} /></div></div>
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New engagement</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Starts from a template with phases, tasks and milestones.</p>
          <div><Label>Template</Label>
            <Select value={form.template_key} onValueChange={(v) => setForm({ ...form, template_key: v })}>
              <SelectTrigger className="mt-1.5" data-testid="eng-template"><SelectValue /></SelectTrigger>
              <SelectContent>{(templates || [{ key: "standard", name: "Standard Sustainability Engagement" }]).map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            {form.template_key === "sgx_issb" && <p className="text-xs text-emerald-600 mt-1.5">Singapore-ready phases for SGX / ISSB reporting.</p>}
          </div>
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="eng-title" className="mt-1.5" /></div>
          <div><Label>Client name</Label><Input value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} data-testid="eng-org" className="mt-1.5" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => create.mutate()} disabled={!form.title || !form.org_name} data-testid="eng-create">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EngagementDetail({ id, onBack }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: e, isLoading } = useQuery({ queryKey: ["engagement", id], queryFn: async () => (await api.get(`/engagements/${id}`)).data });
  const [view, setView] = useState("board");
  const [taskName, setTaskName] = useState("");
  const [taskPhase, setTaskPhase] = useState("");

  const setStatus = useMutation({ mutationFn: (status) => api.patch(`/engagements/${id}/status`, { status }), onSuccess: (r) => qc.setQueryData(["engagement", id], r.data) });
  const updateTask = useMutation({
    mutationFn: ({ tid, status }) => api.patch(`/engagements/${id}/tasks/${tid}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["engagement", id] }),
    onError: (err) => toast.error(err.response?.data?.detail || "Update failed"),
  });
  const addTask = useMutation({ mutationFn: () => api.post(`/engagements/${id}/tasks`, { name: taskName, phase_id: taskPhase || null }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["engagement", id] }); setTaskName(""); toast.success("Task added"); } });
  const approveMilestone = useMutation({ mutationFn: (mid) => api.patch(`/engagements/${id}/milestones/${mid}/approve`), onSuccess: (r) => { qc.setQueryData(["engagement", id], r.data); toast.success("Milestone approved"); } });

  if (isLoading || !e) return <Loading />;
  const phaseTasks = (pid) => e.tasks.filter((t) => t.phase_id === pid);
  const cycleStatus = (t) => { const order = ["todo", "in_progress", "done"]; const next = order[(order.indexOf(t.status) + 1) % 3]; updateTask.mutate({ tid: t.id, status: next }); };

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ChevronLeft className="w-4 h-4" /> Back to engagements</button>
      <PageHeader eyebrow={`${e.template_version} · ${e.tasks.length} tasks`} title={e.title} description={e.org_name}
        actions={
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 text-sm text-slate-600 px-3 py-1.5 rounded-lg bg-white border border-slate-200"><span className={`w-2 h-2 rounded-full ${HEALTH_COLORS[e.health]}`} /> {HEALTH_LABEL[e.health]}</span>
            <Select value={e.status} onValueChange={(v) => setStatus.mutate(v)}>
              <SelectTrigger className="w-36" data-testid="eng-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={() => nav(`/workspace/${id}`)} data-testid="open-workspace" className="bg-emerald-600 hover:bg-emerald-700"><Users2 className="w-4 h-4 mr-1.5" /> Client Workspace</Button>
          </div>
        } />

      <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 w-fit mb-6">
        {[["board", Columns3, "Board"], ["list", ListTodo, "List"], ["timeline", GitBranch, "Timeline"]].map(([v, Icon, label]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`eng-view-${v}`} className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 ${view === v ? "bg-emerald-600 text-white" : "text-slate-600"}`}><Icon className="w-4 h-4" /> {label}</button>
        ))}
      </div>

      {/* add task */}
      <div className="flex gap-2 mb-6">
        <Input value={taskName} onChange={(e2) => setTaskName(e2.target.value)} data-testid="new-task-name" placeholder="Add a task…" className="max-w-xs" onKeyDown={(ev) => { if (ev.key === "Enter" && taskName) addTask.mutate(); }} />
        <Select value={taskPhase} onValueChange={setTaskPhase}>
          <SelectTrigger className="w-40" data-testid="new-task-phase"><SelectValue placeholder="Phase" /></SelectTrigger>
          <SelectContent>{e.phases.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" onClick={() => addTask.mutate()} disabled={!taskName} data-testid="add-task"><Plus className="w-4 h-4" /></Button>
      </div>

      {view === "board" && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {["todo", "in_progress", "done"].map((st) => (
            <div key={st} className="flex-shrink-0 w-72">
              <div className="text-xs font-semibold text-slate-500 mb-3 px-1">{TASK_STATUS[st]} · {e.tasks.filter((t) => t.status === st).length}</div>
              <div className="space-y-2">
                {e.tasks.filter((t) => t.status === st).map((t) => (
                  <button key={t.id} onClick={() => cycleStatus(t)} data-testid={`task-${t.id}`} className="w-full text-left bg-white rounded-xl border border-slate-200/80 shadow-sm p-3.5 hover:border-emerald-200 transition-colors">
                    <div className="flex items-start gap-2">
                      {t.status === "done" ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> : t.status === "in_progress" ? <Clock className="w-4 h-4 text-amber-500 mt-0.5" /> : <Circle className="w-4 h-4 text-slate-300 mt-0.5" />}
                      <span className={`text-sm ${t.status === "done" ? "text-slate-400 line-through" : "text-slate-700"}`}>{t.name}</span>
                    </div>
                    {t.due_date && <div className="text-[11px] text-slate-400 mt-1.5 ml-6">Due {t.due_date}</div>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === "list" && (
        <div className="space-y-6">
          {e.phases.map((ph) => (
            <div key={ph.id} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
              <h3 className="font-display font-semibold text-slate-900 mb-4">{ph.order}. {ph.name}</h3>
              <div className="space-y-2">
                {phaseTasks(ph.id).map((t) => (
                  <div key={t.id} className="flex items-center gap-3 py-1.5">
                    <button onClick={() => cycleStatus(t)} data-testid={`task-toggle-${t.id}`}>
                      {t.status === "done" ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : t.status === "in_progress" ? <Clock className="w-5 h-5 text-amber-500" /> : <Circle className="w-5 h-5 text-slate-300" />}
                    </button>
                    <span className={`text-sm flex-1 ${t.status === "done" ? "text-slate-400 line-through" : "text-slate-700"}`}>{t.name}</span>
                    <span className="text-xs text-slate-400">{t.owner}</span>
                  </div>
                ))}
              </div>
              {e.milestones.filter((m) => m.phase_id === ph.id).map((m) => (
                <div key={m.id} className="flex items-center justify-between mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <span className="flex items-center gap-2 text-sm text-amber-800"><Flag className="w-4 h-4" /> {m.name}</span>
                  {m.approved ? <span className="text-xs text-emerald-700 font-medium flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Approved</span>
                    : <Button size="sm" variant="outline" onClick={() => approveMilestone.mutate(m.id)} data-testid={`approve-milestone-${m.id}`} className="h-7 text-xs">Approve milestone</Button>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {view === "timeline" && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
          <div className="relative pl-6">
            <div className="absolute left-[9px] top-2 bottom-2 w-0.5 bg-slate-200" />
            {e.phases.map((ph) => {
              const pt = phaseTasks(ph.id); const done = pt.filter((t) => t.status === "done").length;
              const pct = pt.length ? Math.round((done / pt.length) * 100) : 0;
              return (
                <div key={ph.id} className="relative mb-6 last:mb-0">
                  <div className={`absolute -left-6 top-1 w-4 h-4 rounded-full border-2 ${pct === 100 ? "bg-emerald-500 border-emerald-500" : "bg-white border-slate-300"}`} />
                  <div className="font-medium text-slate-900">{ph.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{done}/{pt.length} tasks · {pct}%</div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-2 max-w-xs"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
